import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, FileCheck2, ShieldCheck, Upload } from "lucide-react";
import {
  captureReceivedQuotation,
  decideReceivedQuotation,
  quotationEvidenceUrl,
  quotationEvidenceWorkspace,
  uploadQuotationEvidence,
} from "@/lib/api";
import type { QuotationEvidence } from "@/lib/types";
import {
  EVIDENCE_CLASSIFICATIONS,
  EVIDENCE_SOURCES,
  evidenceClassificationForSource,
  marketplaceWarning,
  provenanceBadges,
} from "@/lib/quotation-integrity.mjs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { money, titleCase } from "@/lib/utils";

type Workspace = Awaited<ReturnType<typeof quotationEvidenceWorkspace>>;

function EvidenceBadges({ record }: { record: Record<string, unknown> }) {
  const tones = {
    success: "border-[var(--success-border)] bg-[var(--success-soft)] text-[var(--success)]",
    warning: "border-[var(--warning-border)] bg-[var(--warning-soft)] text-[var(--warning)]",
    error: "border-[var(--error-border)] bg-[var(--error-soft)] text-[var(--error)]",
    info: "border-[var(--info-border)] bg-[var(--info-soft)] text-[var(--info)]",
  };
  return (
    <div className="flex flex-wrap gap-1.5" aria-label="Quotation provenance">
      {provenanceBadges(record).map((badge) => (
        <span key={badge.value} className={`rounded-md border px-2 py-1 text-[11px] font-semibold ${tones[badge.tone as keyof typeof tones]}`}>
          {badge.label}
        </span>
      ))}
    </div>
  );
}

function Field({ label, name, type = "text", required = false, placeholder }: { label: string; name: string; type?: string; required?: boolean; placeholder?: string }) {
  return <Label>{label}<Input name={name} type={type} required={required} placeholder={placeholder} /></Label>;
}

function optionalNumber(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text === "" ? null : Number(text);
}

export function QuotationEvidenceDialog({
  rfqId,
  organisationId,
  canCapture,
  canVerify,
  onOpenChange,
  onDone,
}: {
  rfqId?: string;
  organisationId: string;
  canCapture: boolean;
  canVerify: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [workspace, setWorkspace] = useState<Workspace>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File>();
  const [source, setSource] = useState("email");
  const [classification, setClassification] = useState("buyer_recorded_quotation");
  const [selectedEvidence, setSelectedEvidence] = useState<QuotationEvidence>();
  const [decisionReason, setDecisionReason] = useState("");
  const [dropActive, setDropActive] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    if (!rfqId) return;
    try {
      setWorkspace(await quotationEvidenceWorkspace(organisationId, rfqId));
    } catch (reason) {
      setError((reason as Error).message);
    }
  }, [organisationId, rfqId]);
  useEffect(() => { void refresh(); }, [refresh]);

  const lines = workspace?.rfq.requisitions.requisition_lines || [];
  const forcedClassification = evidenceClassificationForSource(source, classification);
  const marketMode = forcedClassification === "market_price_reference";
  const currentEvidence = useMemo(
    () => workspace?.evidence.filter((item) => item.is_current) || [],
    [workspace],
  );

  function acceptFile(candidate?: File) {
    if (!candidate) return;
    setFile(candidate);
    setError("");
  }
  function drop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault();
    setDropActive(false);
    acceptFile(event.dataTransfer.files[0]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rfqId || !file) {
      setError("Upload the original evidence before saving the draft.");
      return;
    }
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const recordedLines = lines
      .filter((line: any) => form.get(`include-${line.id}`))
      .map((line: any) => ({
        requisition_line_id: line.id,
        quantity: optionalNumber(form.get(`quantity-${line.id}`)) ?? line.quantity,
        unit: String(form.get(`unit-${line.id}`) || line.unit || ""),
        unit_price: optionalNumber(form.get(`price-${line.id}`)),
        brand: String(form.get(`brand-${line.id}`) || ""),
        model: String(form.get(`model-${line.id}`) || ""),
        technical_specification: String(form.get(`spec-${line.id}`) || line.specification || ""),
        alternate_product: Boolean(form.get(`alternate-${line.id}`)),
        complies: Boolean(form.get(`complies-${line.id}`)),
        remarks: String(form.get(`remarks-${line.id}`) || ""),
      }));
    const recordedFields = {
      supplier_name: String(form.get("supplierName") || ""),
      quote_number: String(form.get("quoteNumber") || ""),
      quote_date: String(form.get("quoteDate") || ""),
      valid_until: String(form.get("validUntil") || ""),
      currency: String(form.get("currency") || "").toUpperCase(),
      delivery_days: optionalNumber(form.get("deliveryDays")),
      commercial_mode: String(form.get("commercialMode") || "partial"),
      base_price: optionalNumber(form.get("basePrice")),
      discount: optionalNumber(form.get("discount")),
      tax: optionalNumber(form.get("tax")),
      freight: optionalNumber(form.get("freight")),
      installation: optionalNumber(form.get("installation")),
      other_charges: optionalNumber(form.get("otherCharges")),
      total_landed_cost: optionalNumber(form.get("total")),
      payment_terms: String(form.get("paymentTerms") || ""),
      warranty: String(form.get("warranty") || ""),
      amc_cmc: String(form.get("amcCmc") || ""),
      exclusions: String(form.get("exclusions") || ""),
      qualifications: String(form.get("qualifications") || ""),
      lines: recordedLines,
    };
    setBusy(true);
    setError("");
    try {
      const uploaded = await uploadQuotationEvidence(organisationId, rfqId, file);
      await captureReceivedQuotation(organisationId, {
        p_rfq_id: rfqId,
        p_vendor_id: form.get("vendor") || null,
        p_source_channel: source,
        p_classification: forcedClassification,
        p_original_attachment_id: uploaded.attachment.id,
        p_sender_email: form.get("senderEmail") || null,
        p_sender_phone: form.get("senderPhone") || null,
        p_sender_contact_name: form.get("senderContact") || null,
        p_physical_source: form.get("physicalSource") || null,
        p_claimed_quote_date: form.get("quoteDate") || null,
        p_claimed_received_at: form.get("claimedReceivedAt") || null,
        p_source_url: form.get("sourceUrl") || null,
        p_marketplace_details: marketMode ? {
          marketplace: form.get("marketplace"), product_title: form.get("productTitle"),
          seller: form.get("seller"), sku: form.get("sku"), availability: form.get("availability"),
          listed_price: form.get("total"), captured_at: new Date().toISOString(),
        } : {},
        p_extraction_state: "unavailable",
        p_extracted_fields: {},
        p_field_confidence: {},
        p_recorded_fields: recordedFields,
        p_corrections: [],
        p_amends_evidence_id: form.get("amendsEvidence") || null,
      });
      formElement.reset();
      setFile(undefined);
      await refresh();
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function decide(decision: string) {
    if (!selectedEvidence || !decisionReason.trim()) {
      setError("A decision reason is required for the immutable audit record.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await decideReceivedQuotation(organisationId, selectedEvidence.id, decision, decisionReason);
      setDecisionReason("");
      setSelectedEvidence(undefined);
      await refresh();
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function openOriginal(record: QuotationEvidence) {
    if (!record.object_path) return setError("Original object path is not available.");
    try { window.open(await quotationEvidenceUrl(record.object_path), "_blank", "noopener,noreferrer"); }
    catch (reason) { setError((reason as Error).message); }
  }

  return (
    <Dialog open={Boolean(rfqId)} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(1180px,calc(100vw-1rem))]">
        <DialogHeader eyebrow="ASSISTED CAPTURE" title={canCapture ? "Record received quotation" : "Review quotation evidence"} description="Preserve external evidence first. Upload does not admit a bid, approve an award or change supplier bank details." />
        <div className={`grid gap-6 p-4 md:p-6 ${canCapture ? "lg:grid-cols-[1.15fr_.85fr]" : "lg:grid-cols-1"}`}>
          {canCapture && <form onSubmit={submit} className="grid min-w-0 gap-5">
            <section className="grid gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
              <h3 className="font-semibold">1. Original immutable evidence</h3>
              <input ref={fileRef} className="sr-only" type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx" onChange={(event) => acceptFile(event.target.files?.[0])} aria-label="Upload original quotation evidence" />
              <button type="button" onClick={() => fileRef.current?.click()} onDragOver={(event) => { event.preventDefault(); setDropActive(true); }} onDragLeave={() => setDropActive(false)} onDrop={drop} className={`grid min-h-32 place-items-center rounded-xl border-2 border-dashed p-5 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] ${dropActive ? "border-[var(--action)] bg-[var(--action-soft)]" : "border-[var(--border-strong)] bg-[var(--surface-secondary)]"}`}>
                <span><Upload className="mx-auto mb-2 size-6 text-[var(--action)]" /><strong className="block">Drop a PDF, scan, screenshot or photo</strong><small className="text-[var(--text-secondary)]">Choose a PDF or document, or select a photo from your device. The original is hashed and cannot be replaced.</small>{file && <span className="mt-2 block text-sm font-semibold text-[var(--action-hover)]">{file.name} · {(file.size / 1024).toFixed(1)} KB</span>}</span>
              </button>
              <div className="grid gap-3 sm:grid-cols-2">
                <Label>Source channel<Select value={source} onChange={(event) => setSource(event.target.value)}>{EVIDENCE_SOURCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Label>
                <Label>Evidence classification<Select value={classification} onChange={(event) => setClassification(event.target.value)}>{EVIDENCE_CLASSIFICATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Label>
                <Label>Supplier, where applicable<Select name="vendor"><option value="">No supplier / market reference</option>{workspace?.vendors.map((vendor) => <option key={vendor.id} value={vendor.id}>{vendor.name}</option>)}</Select></Label>
                <Field label="Supplier name shown on evidence" name="supplierName" />
                <Field label="Original sender email" name="senderEmail" type="email" />
                <Field label="WhatsApp / originating number" name="senderPhone" type="tel" />
                <Field label="Contact name" name="senderContact" />
                <Field label="Physical source or representative" name="physicalSource" />
                <Field label="Claimed receipt date and time" name="claimedReceivedAt" type="datetime-local" />
                <Field label="Source URL" name="sourceUrl" type="url" />
                <Label>Amends earlier evidence<Select name="amendsEvidence"><option value="">No — original version</option>{currentEvidence.filter((item) => item.vendor_id).map((item) => <option key={item.id} value={item.id}>{item.vendor_name || "Supplier"} · v{item.version_number} · {item.original_file_name}</option>)}</Select></Label>
              </div>
              {marketMode && <div className="grid gap-3 rounded-lg border border-[var(--info-border)] bg-[var(--info-soft)] p-3 sm:grid-cols-2"><Field label="Marketplace" name="marketplace" placeholder="Amazon Business" /><Field label="Product title" name="productTitle" /><Field label="Seller" name="seller" /><Field label="Brand / model / SKU" name="sku" /><Field label="Availability / delivery estimate" name="availability" /></div>}
            </section>

            <section className="grid gap-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
              <div><h3 className="font-semibold">2. Human-recorded quotation fields</h3><p className="text-sm text-[var(--text-secondary)]">OCR or parser is not configured in this release. No value is inferred: compare every field with the original evidence.</p></div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Quote number" name="quoteNumber" /><Field label="Quote date" name="quoteDate" type="date" /><Field label="Valid until" name="validUntil" type="date" />
                <Field label="Currency (ISO)" name="currency" placeholder="INR" /><Field label="Delivery days" name="deliveryDays" type="number" /><Label>Commercial condition<Select name="commercialMode"><option value="partial">Partial quotation allowed</option><option value="all_or_nothing">All or nothing</option></Select></Label>
                <Field label="Base price" name="basePrice" type="number" /><Field label="Discount" name="discount" type="number" /><Field label="GST / tax" name="tax" type="number" />
                <Field label="Freight" name="freight" type="number" /><Field label="Installation" name="installation" type="number" /><Field label="Other charges" name="otherCharges" type="number" />
                <Field label="Total landed cost" name="total" type="number" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2"><Label>Payment terms<Textarea name="paymentTerms" /></Label><Label>Warranty<Textarea name="warranty" /></Label><Label>AMC / CMC<Textarea name="amcCmc" /></Label><Label>Exclusions and qualifications<Textarea name="exclusions" /><Textarea className="mt-2" name="qualifications" placeholder="Notes and qualifications" /></Label></div>
              <div className="overflow-x-auto rounded-lg border border-slate-200"><table className="w-full min-w-[850px] text-sm"><thead className="bg-slate-50 text-left"><tr>{["Include","Requirement","Qty / unit","Unit price","Brand / model","Specification","Controls"].map((head) => <th key={head} className="p-2">{head}</th>)}</tr></thead><tbody>{lines.map((line: any) => <tr key={line.id} className="border-t border-slate-100 align-top"><td className="p-2"><input name={`include-${line.id}`} type="checkbox" defaultChecked aria-label={`Include ${line.name}`} /></td><td className="p-2 font-medium">{line.name}</td><td className="p-2"><Input className="w-20" name={`quantity-${line.id}`} type="number" step=".01" defaultValue={line.quantity} aria-label={`${line.name} quantity`} /><Input className="mt-1 w-20" name={`unit-${line.id}`} defaultValue={line.unit} aria-label={`${line.name} unit`} /></td><td className="p-2"><Input className="w-28" name={`price-${line.id}`} type="number" step=".01" aria-label={`${line.name} unit price`} /></td><td className="p-2"><Input name={`brand-${line.id}`} placeholder="Brand" aria-label={`${line.name} brand`} /><Input className="mt-1" name={`model-${line.id}`} placeholder="Model" aria-label={`${line.name} model`} /></td><td className="p-2"><Textarea name={`spec-${line.id}`} defaultValue={line.specification} aria-label={`${line.name} specification`} /></td><td className="p-2"><label className="block"><input name={`complies-${line.id}`} type="checkbox" /> Meets spec</label><label className="mt-2 block"><input name={`alternate-${line.id}`} type="checkbox" /> Alternate</label><Input className="mt-2" name={`remarks-${line.id}`} placeholder="Remarks" aria-label={`${line.name} remarks`} /></td></tr>)}</tbody></table></div>
              <label className="flex items-start gap-2 rounded-lg border border-[var(--warning-border)] bg-[var(--warning-soft)] p-3 text-sm text-[var(--warning)]"><input required type="checkbox" className="mt-1" /><span><strong>Verification checklist:</strong> I compared the recorded fields with the original evidence. Missing values remain blank; no commercial or tax value was invented.</span></label>
              <div className="flex justify-end"><Button disabled={busy} type="submit"><FileCheck2 className="size-4" />Save evidence for verification</Button></div>
            </section>
          </form>}

          <aside className="grid content-start gap-4">
            {error && <p role="alert" className="rounded-lg border border-[var(--error-border)] bg-[var(--error-soft)] p-3 text-sm text-[var(--error)]">{error}</p>}
            <div className="rounded-xl border border-[var(--action-border)] bg-[var(--action-soft)] p-4 text-sm text-[var(--action-hover)]"><ShieldCheck className="mb-2 size-5" /><strong>Maker–checker boundary</strong><p className="mt-1 text-[var(--text-secondary)]">The uploader is the maker. Only a designated verifier may admit or reject buyer-recorded evidence. Financial approval authority remains separate.</p></div>
            <section className="rounded-xl border border-slate-200"><div className="border-b border-slate-200 p-4"><h3 className="font-semibold">Evidence register</h3><p className="text-sm text-slate-500">Formal bids and market references remain visibly separate.</p></div><div className="max-h-[720px] divide-y divide-slate-100 overflow-y-auto">{currentEvidence.length ? currentEvidence.map((record) => { const warning = marketplaceWarning(record); return <article key={record.id} className="grid gap-3 p-4"><div className="flex items-start justify-between gap-3"><div><strong>{record.vendor_name || (marketMode ? "Market reference" : "Unidentified supplier")}</strong><small className="block text-slate-500">v{record.version_number} · {record.original_file_name}</small></div><Button size="sm" variant="outline" onClick={() => void openOriginal(record)} aria-label={`Open original ${record.original_file_name}`}><ExternalLink className="size-4" /></Button></div><EvidenceBadges record={record as unknown as Record<string, unknown>} />{warning && <div className="flex gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-900"><AlertTriangle className="size-4 shrink-0" />{warning}</div>}{record.integrity_flags?.length ? <div className="rounded-lg border border-amber-200 bg-amber-50 p-3"><strong className="text-xs text-amber-900">Review required</strong>{record.integrity_flags.map((flag: any) => <p key={flag.id} className="mt-1 text-xs text-amber-900">{flag.title}: {flag.explanation}</p>)}</div> : <p className="flex items-center gap-2 text-xs text-emerald-700"><CheckCircle2 className="size-4" />No recorded integrity flags</p>}<dl className="grid grid-cols-2 gap-2 text-xs"><div><dt className="text-slate-500">Recorded total</dt><dd className="font-semibold tabular-nums">{record.recorded_fields?.currency || ""} {record.recorded_fields?.total_landed_cost == null ? "NOT AVAILABLE" : money(record.recorded_fields.total_landed_cost)}</dd></div><div><dt className="text-slate-500">Comparison</dt><dd>{titleCase(record.comparison_state)}</dd></div><div><dt className="text-slate-500">Supplier confirmation</dt><dd>{titleCase(record.supplier_confirmation_state)}</dd></div><div><dt className="text-slate-500">Captured</dt><dd>{new Date(record.actual_received_at).toLocaleString()}</dd></div></dl>{canVerify && !["admitted","rejected","market_reference"].includes(record.verification_state) && <Button size="sm" variant="outline" onClick={() => setSelectedEvidence(record)}>Review admission</Button>}</article>; }) : <p className="p-6 text-sm text-slate-500">No external quotation evidence has been recorded for this RFQ.</p>}</div></section>
          </aside>
        </div>

        {selectedEvidence && <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 shadow-lg"><div className="mx-auto grid max-w-5xl gap-3"><div><strong>Admission decision · {selectedEvidence.vendor_name || "Market reference"}</strong><p className="text-xs text-slate-500">Supplier confirmation strengthens provenance but never replaces internal verification or approval.</p></div><Textarea value={decisionReason} onChange={(event) => setDecisionReason(event.target.value)} placeholder="Required decision reason" aria-label="Quotation verification decision reason" /><div className="flex flex-wrap gap-2"><Button disabled={busy} onClick={() => void decide("admit")}>Admit to comparison</Button><Button disabled={busy} variant="outline" onClick={() => void decide("classify_market_reference")}>Keep as market reference</Button><Button disabled={busy} variant="outline" onClick={() => void decide("request_supplier_confirmation")}>Prepare supplier confirmation</Button>{selectedEvidence.late_submission && <Button disabled={busy} variant="outline" onClick={() => void decide("approve_late_exception")}>Approve late exception</Button>}<Button disabled={busy} variant="destructive" onClick={() => void decide("reject")}>Reject evidence</Button><Button variant="ghost" onClick={() => setSelectedEvidence(undefined)}>Cancel</Button></div></div></div>}
      </DialogContent>
    </Dialog>
  );
}
